import { type JSX, useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router";

import { useGetOwnerDetails } from "~/v2/api/hooks";
import type { Account, Beneficiary, OwnerDetails } from "~/v2/api/rest/account";
import { OnboardingLayout } from "~/v2/components/OnboardingLayout";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { AddBeneficiaries } from "./addBeneficiaries";
import { Agreements } from "./agreements";
import { ConfirmInformation } from "./confirmInformation";
import { IneligibleScreen } from "./IneligibleScreen";
import { SelectAccountSubType } from "./selectAccountSubType";
import { StockLending } from "./stockLending";
import { useCancelModal } from "./useCancelModal";
import { useIsEligibleForIRAOnboarding } from "./util";

export const IRA_ONBOARDING_STEP = [
  "type",
  "information",
  "beneficiaries",
  "stock_lending",
  "agreements",
] as const;

export type IRAStep = (typeof IRA_ONBOARDING_STEP)[number];

const STEPS: Record<
  IRAStep,
  {
    title: string;
    description: string;
    component: (props: IRAOnboardingProps) => JSX.Element;
    step?: number;
    path: string;
  }
> = {
  type: {
    title: "Which IRA is right for you?",
    description: "Choose the type of IRA you'd like to open to continue.",
    component: SelectAccountSubType,
    path: Path.ROUTE_IRA_ONBOARDING_SELECT_TYPE,
  },
  information: {
    title: "Confirm your Information",
    description:
      "Please review your information and fill in the remaining fields to continue.",
    component: ConfirmInformation,
    step: 1,
    path: Path.ROUTE_IRA_ONBOARDING_CONFIRM_INFORMATION,
  },
  beneficiaries: {
    title: "Add Beneficiaries",
    description:
      "Add up to 6 beneficiaries who will receive the assets from your IRA. You can add or update beneficiaries at any time.",
    component: AddBeneficiaries,
    step: 2,
    path: Path.ROUTE_IRA_ONBOARDING_BENEFICIARIES,
  },
  stock_lending: {
    title: "Stock Lending Program",
    description:
      "Enroll to earn extra income by lending out eligible stocks or ETFs in your account. You'll still own your investments and can sell them anytime.",
    component: StockLending,
    step: 3,
    path: Path.ROUTE_IRA_ONBOARDING_STOCK_LENDING,
  },
  agreements: {
    title: "Last step",
    description: "Please review and accept the following agreements to finish.",
    component: Agreements,
    step: 4,
    path: Path.ROUTE_IRA_ONBOARDING_AGREEMENTS,
  },
};

export type IRAOnboardingState = {
  step: IRAStep | null;
  subType: "traditional" | "roth" | null;
  isLoading: boolean;
  beneficiaries: Beneficiary[];
  isStockLendingEnabled?: boolean;
};

export interface IRAOnboardingProps extends IRAOnboardingState {
  tradeAccount: Account;
  tradeAccountDetails: OwnerDetails;
  traditionalIraAccount?: Account;
  rothIraAccount?: Account;
  refetchTradeAccountDetails: () => void;
  refetchTradeAccount: () => void;
  onSaveSessionState: (state: Partial<IRAOnboardingState>) => void;
  onDeleteSessionState: () => void;
}

const DEFAULT_STATE: IRAOnboardingState = {
  step: "type",
  subType: null,
  isLoading: false,
  beneficiaries: [],
  isStockLendingEnabled: true,
};

const IRAOnboarding = () => {
  const history = useHistory();

  const [sessionState, setSessionState] = useState<IRAOnboardingState>({
    step: null,
    subType: null,
    isLoading: true,
    beneficiaries: [],
    isStockLendingEnabled: true,
  });

  const { liveAccounts: accounts, refetchLiveAccounts } = useAccountContext();

  const { tradeAccount, rothIraAccount, traditionalIraAccount } = useMemo(
    () => ({
      tradeAccount: accounts?.find(
        (account) => account.account_type === "trading",
      ),
      rothIraAccount: accounts?.find(
        (account) =>
          account.account_type === "ira" && account.account_sub_type === "roth",
      ),
      traditionalIraAccount: accounts?.find(
        (account) =>
          account.account_type === "ira" &&
          account.account_sub_type === "traditional",
      ),
    }),
    [accounts],
  );

  const { details: tradeAccountDetails, refetch: refetchTradeAccountDetails } =
    useGetOwnerDetails();

  const syncPathnameToStep = (step: IRAStep | null) =>
    window?.history?.replaceState(null, "", STEPS[step ?? "type"].path);

  const cancelModal = useCancelModal({
    onCancel: () => cancelModal.toggle(),
    onConfirm: () => history.push(Path.ROUTE_DASHBOARD),
  });

  const onSaveSessionState = (newState: Partial<IRAOnboardingState>) => {
    setSessionState((prevState) => {
      const state = { ...prevState, ...newState };

      if (tradeAccount) {
        window?.sessionStorage?.setItem(
          `new-ira-account-${tradeAccount.id}`,
          JSON.stringify(state),
        );
      }

      syncPathnameToStep(state.step);
      return state;
    });
  };

  const onDeleteSessionState = () => {
    window?.sessionStorage?.removeItem(`new-ira-account-${tradeAccount?.id}`);
    setSessionState(DEFAULT_STATE);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: the two id fields are the triggers that reload the persisted session for a different account; the body reads them only to guard. It writes state, so depending on the fetched objects themselves would loop.
  useEffect(() => {
    if (!tradeAccount?.id || !tradeAccountDetails?.owner_id) {
      return;
    }

    try {
      const sessionString = window?.sessionStorage?.getItem(
        `new-ira-account-${tradeAccount.id}`,
      );

      let session: IRAOnboardingState = sessionString
        ? JSON.parse(sessionString)
        : { ...DEFAULT_STATE };

      if (
        !session ||
        !IRA_ONBOARDING_STEP.includes(session?.step as IRAStep) ||
        !(session?.subType === "roth" || session?.subType === "traditional")
      ) {
        session = { ...DEFAULT_STATE };
      }

      syncPathnameToStep(session.step);
      setSessionState({ ...session, isLoading: false });
    } catch (error) {
      console.error("session state error", error);
      setSessionState(DEFAULT_STATE);
    }
  }, [tradeAccount?.id, tradeAccountDetails?.owner_id]);

  const { isEligible, isLoading: isEligibilityLoading } =
    useIsEligibleForIRAOnboarding();

  const step = sessionState.step ? STEPS[sessionState.step] : undefined;
  const StepComponent = step?.component;

  if (!sessionState.isLoading && !isEligibilityLoading && !isEligible) {
    return (
      <IneligibleScreen onExit={() => history.push(Path.ROUTE_DASHBOARD)} />
    );
  }

  return (
    <OnboardingLayout
      isLoading={sessionState.isLoading || isEligibilityLoading}
      onCancel={() => cancelModal.toggle()}
      title={step?.title}
      description={step?.description}
      currentStep={step?.step}
      totalSteps={4}
    >
      {tradeAccount && tradeAccountDetails && StepComponent && (
        <StepComponent
          {...sessionState}
          tradeAccount={tradeAccount}
          tradeAccountDetails={tradeAccountDetails}
          rothIraAccount={rothIraAccount}
          traditionalIraAccount={traditionalIraAccount}
          refetchTradeAccountDetails={refetchTradeAccountDetails}
          refetchTradeAccount={refetchLiveAccounts}
          onSaveSessionState={onSaveSessionState}
          onDeleteSessionState={onDeleteSessionState}
        />
      )}
      {cancelModal.component}
    </OnboardingLayout>
  );
};

export default IRAOnboarding;
